import { createContext, type ReactNode, useContext } from "react";

/**
 * Site variant for regional content adaptation
 * - "uk": British audience (understated tone, British spelling)
 * - "us": American audience (default)
 */
export type SiteVariant = "uk" | "us";

// Extend Window interface for the bootstrap script's global
declare global {
  interface Window {
    __SITE_VARIANT__?: SiteVariant;
  }
}

/**
 * Get variant at module load time (before any component renders).
 * This runs once when the module is first imported.
 * The value comes from the inline bootstrap script in index.html.
 */
const getInitialVariant = (): SiteVariant => {
  if (typeof window !== "undefined" && window.__SITE_VARIANT__) {
    return window.__SITE_VARIANT__;
  }
  return "us"; // Default fallback
};

// Initialize at module load (before React renders)
const initialVariant = getInitialVariant();

interface VariantContextType {
  /** Current site variant */
  variant: SiteVariant;
  /** Convenience boolean for UK variant */
  isUK: boolean;
  /** Convenience boolean for US variant */
  isUS: boolean;
}

const VariantContext = createContext<VariantContextType | undefined>(undefined);

interface VariantProviderProps {
  children: ReactNode;
  /** Override variant for testing - bypasses the global */
  testVariant?: SiteVariant;
}

/**
 * VariantProvider - wraps app to provide regional variant context
 *
 * Note: The actual variant is determined by the bootstrap script in index.html
 * before React loads. This provider just makes it available to components.
 */
export const VariantProvider = ({
  children,
  testVariant,
}: VariantProviderProps) => {
  const variant = testVariant ?? initialVariant;

  return (
    <VariantContext
      value={{
        isUK: variant === "uk",
        isUS: variant === "us",
        variant,
      }}
    >
      {children}
    </VariantContext>
  );
};

/**
 * Hook for consuming variant in components
 *
 * @example
 * ```tsx
 * const { isUK, variant } = useVariant();
 * return <h1>{isUK ? "Hello" : "Hey"}, I'm Tony</h1>;
 * ```
 */
export const useVariant = (): VariantContextType => {
  const context = useContext(VariantContext);
  if (context === undefined) {
    throw new Error("useVariant must be used within a VariantProvider");
  }
  return context;
};
